<template>
    <template v-if="cart?.lineItems && cart?.lineItems?.length > 0">
        <div
            class="w-full text-sm text-gray-500 px-4 font-light"
            :class="{
                '': !isInteractive,
                'lg:p-6 lg:pb-0': isInteractive
            }"
        >
            <div
                class="flex flex-col gap-2"
                :class="{
                    'p-4 pb-5': !isInteractive,
                    'pt-8 lg:pt-0': isInteractive
                }"
            >
                <div v-if="!hideSubtotal" class="flex justify-between items-center text-base-content text-sm">
                    <div class="flex gap-3 items-baseline">
                        <div class="font-semibold">
                            {{ $t('cart.totals.subtotal') }}
                        </div>
                    </div>
                    <div class="font-semibold" data-cy="cartSubtotals">
                        {{ getFormattedPrice(cart?.price?.positionPrice, { forceFraction: true }) }}
                    </div>
                </div>

                <template v-if="cart?.deliveries">
                    <div v-if="cart?.deliveries?.length > 1">
                        <div
                            v-for="(delivery, index) in cart?.deliveries"
                            :key="index"
                            class="flex justify-between items-center text-base-content text-sm"
                        >
                            <div>{{ $t('cart.totals.shipping') }}</div>
                            <div data-cy="cartTotalsShipping">{{ getFormattedPrice(delivery.shippingCosts?.totalPrice, { forceFraction: true }) }}</div>
                        </div>
                    </div>
                    <div
                        v-else-if="cart?.deliveries?.length === 1"
                        class="flex justify-between items-center text-base-content text-sm"
                    >
                        <div>{{ $t('cart.totals.shipping') }}</div>
                        <div data-cy="cartTotalsShipping">{{ getFormattedPrice(cart?.deliveries[0]?.shippingCosts?.totalPrice, { forceFraction: true }) }}</div>
                    </div>
                </template>

                <div class="flex justify-between items-center text-base-content">
                    <div class="flex gap-3 items-baseline">
                        <div class="text-lg font-semibold">
                            {{ $t('cart.totals.total') }}
                        </div>
                        <div class="text-xs text-base-content-subtle font-normal">
                            <client-only>
                                <span v-if="isDisplayGross">
                                    {{ $t('cart.totals.includingTax') }}
                                </span>
                                <span v-else>
                                    {{ $t('cart.totals.excludingTax') }}
                                </span>
                            </client-only>
                        </div>
                    </div>
                    <div class="text-lg font-semibold">
                        {{ getFormattedPrice(cart?.price?.totalPrice, { forceFraction: true }) }}
                    </div>
                </div>

                <div v-if="savingTotal != null" class="flex gap-1 w-fit mt-2 mb-3">
                    <div class="text-sm font-semibold bg-primary rounded-xs text-base-100 px-3 py-1">
                        🎉 {{ $t('cart.totals.saleText') }} {{ getFormattedPrice(savingTotal, { forceFraction: true }) }}
                    </div>
                </div>
            </div>
        </div>
    </template>
</template>

<script setup lang="ts">
const { cart } = useCart()
const { getFormattedPrice } = usePrice()
const { sessionContext } = useSessionContext()

const isDisplayGross = computed(() => sessionContext.value?.currentCustomerGroup?.displayGross !== false)

interface CartTotalsProps {
    isInteractive?: boolean,
    hideSubtotal?: boolean,
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = withDefaults(defineProps<CartTotalsProps>(), {
    isInteractive: true,
    hideSubtotal: false,
})

const savingTotal: Ref<number | null> = computed(() => {
    if (!cart?.value?.lineItems) {
        return null
    }

    let total = 0
    cart?.value?.lineItems.map((lineItem) => {
        const discount = lineItem.price?.listPrice?.discount != null && Number(lineItem.price?.listPrice?.discount * lineItem.quantity * -1)
        if(discount) {
            total += discount
        }
    })
    return total !== 0 ? total : null
})
</script>
